/**
 * The player's say over their data (the privacy page, `screens/Privacy.tsx`):
 *
 * - **Count my plays** (`settings.analytics`, Settings and the privacy page): on by
 *   default. Off: no analytics events are sent (anything queued is dropped), the device
 *   isn't registered (`POST /v1/players`), and solves stay on the device unless a
 *   challenge needs them: one played from a challenge, or the one a challenge is made
 *   from at "Challenge a friend" (sync.ts). The choice itself is never tracked.
 * - **Clear Sparrow's data on this device**: every `arrow:` key in localStorage and
 *   sessionStorage (games, streak, stats, settings, the device id, the queues). The
 *   cached app files stay, so Sparrow still opens offline.
 */
import { settings } from '../app/settings.ts';
import type { KeyValueStore } from '../storage/kv.ts';
import { STORAGE_PREFIX } from '../storage/persistence.ts';
import { platformKeys, platformStore } from './config.ts';
import { needsSending, solveSync } from './sync.ts';

/** Turns usage counts on or off, dropping anything queued that the new choice wouldn't send. */
export function setUsageCounts(on: boolean, store: KeyValueStore = platformStore): void {
	settings().set('analytics', on);
	// Off: what's queued never goes. On: solves held while it was off stay on the device.
	if (!on) store.remove(platformKeys.events);
	solveSync.prune(needsSending);
}

/** Every Sparrow key in `storage`, removed. Never throws. */
function clearStorage(storage: () => Storage | undefined): number {
	let removed = 0;
	try {
		const s = storage();
		if (!s) return 0;
		const keys: string[] = [];
		for (let i = 0; i < s.length; i++) {
			const k = s.key(i);
			if (k !== null && k.startsWith(STORAGE_PREFIX)) keys.push(k);
		}
		for (const k of keys) {
			s.removeItem(k);
			removed++;
		}
	} catch {
		// Blocked storage: nothing to clear.
	}
	return removed;
}

/**
 * Clears everything Sparrow keeps on this device (localStorage and sessionStorage).
 * The caller reloads, so nothing in memory writes it back. Returns how many keys went.
 */
export function clearDeviceData(): number {
	const local = clearStorage(() => (typeof window === 'undefined' ? undefined : window.localStorage));
	const session = clearStorage(() => (typeof window === 'undefined' ? undefined : window.sessionStorage));
	return local + session;
}
